import { cloneRecord, isRecord } from './record';
import type { LiveWireReaction, ReactionDeltaAction, WireMessage } from './types';

/** Returns the wire reaction array after applying one create/destroy delta. */
export function patchWireReactions(
  reactions: WireMessage['reactions'],
  action: ReactionDeltaAction,
  reaction: LiveWireReaction
): WireMessage['reactions'] {
  if (action === 'destroy') {
    const next = reactions.filter((candidate) => candidate.id !== reaction.id);
    return next.length === reactions.length ? reactions : next;
  }

  const index = reactions.findIndex((candidate) => candidate.id === reaction.id);
  if (index < 0) return [...reactions, { ...reaction }];

  return reactions.map((candidate, candidateIndex) => (
    candidateIndex === index ? { ...reaction } : candidate
  ));
}

/** Returns a renderer row with its legacy reaction payload patched for one live delta. */
export function patchRendererRowReaction(
  wire: WireMessage,
  action: ReactionDeltaAction,
  reaction: LiveWireReaction
): unknown {
  const base = isRecord(wire.rendererRow) ? cloneRecord(wire.rendererRow) : rendererRowForWire(wire);
  const nextReactions = patchLegacyRendererReactions(base.reactions, action, reaction);
  if (nextReactions === base.reactions) return wire.rendererRow;
  return {
    ...base,
    reactions: nextReactions
  };
}

function patchLegacyRendererReactions(
  reactions: unknown,
  action: ReactionDeltaAction,
  reaction: LiveWireReaction
): unknown {
  const row = legacyReactionRow(reaction);

  if (Array.isArray(reactions)) {
    if (action === 'destroy') {
      const next = reactions.filter((candidate) => reactionIdForLegacyReaction(candidate) !== reaction.id);
      return next.length === reactions.length ? reactions : next;
    }

    const index = reactions.findIndex((candidate) => reactionIdForLegacyReaction(candidate) === reaction.id);
    if (index < 0) return [...reactions, row];
    return reactions.map((candidate, candidateIndex) => (candidateIndex === index ? row : candidate));
  }

  if (isRecord(reactions)) {
    if (action === 'destroy') {
      if (!Object.hasOwn(reactions, reaction.id)) return reactions;
      const next = { ...reactions };
      delete next[reaction.id];
      return next;
    }

    return {
      ...reactions,
      [reaction.id]: row
    };
  }

  return action === 'create' ? [row] : reactions ?? [];
}

function reactionIdForLegacyReaction(reaction: unknown): string | undefined {
  if (!isRecord(reaction)) return undefined;
  const id = reaction.id;
  return typeof id === 'string' ? id : undefined;
}

function legacyReactionRow(reaction: LiveWireReaction): Record<string, unknown> {
  return {
    id: reaction.id,
    workspace_id: reaction.workspaceId,
    channel_id: reaction.channelId,
    message_id: reaction.messageId,
    profile_id: reaction.profileId,
    emoji_id: reaction.emojiId,
    sort_order: reaction.sortOrder,
    pos_x: reaction.posX ?? null,
    pos_y: reaction.posY ?? null,
    scale: reaction.scale ?? null,
    angle: reaction.angle ?? null,
    ...(reaction.clientRequestId === undefined ? {} : { optimistic_sort_order: reaction.clientRequestId })
  };
}

function rendererRowForWire(wire: WireMessage): Record<string, unknown> {
  return {
    id: wire.id,
    sort_order: wire.sortOrder,
    timestamp: timestampSeconds(wire.createdAt),
    author_id: wire.authorId,
    author_type: 'profile',
    text: wire.bodyHtml ?? wire.body,
    flags: wire.flags ?? [],
    attachments: [],
    link_previews: [],
    contains_only_attachments: wire.containsOnlyAttachments ?? false,
    contains_only_entities: wire.containsOnlyEntities ?? false,
    contains_only_emoji_entities: wire.containsOnlyEmojiEntities ?? false,
    reactions: wire.reactions.map((candidate) => legacyReactionRow({
      ...candidate,
      workspaceId: wire.workspaceId,
      channelId: wire.channelId,
      messageId: wire.id
    }))
  };
}

function timestampSeconds(createdAt: string): number | undefined {
  const milliseconds = Date.parse(createdAt);
  return Number.isFinite(milliseconds) ? Math.floor(milliseconds / 1000) : undefined;
}
